/**
 * The built-in sink / washbasin (defs/sink.ts) as a data spec: a wall
 * fixture (POINT anchor + WALL constraint) whose whole drawing is
 * proportional to its footprint, so it is authored once at the default size
 * in a single `stretch` group:
 *  - the counter outline: the full footprint;
 *  - the basin: an ellipse 70 % of the width and 65 % of the depth, biased
 *    slightly toward the room, drawn as a closed 20-point polyline (the
 *    code's tessellation);
 *  - the tap: a small box against the wall (−n) with a spout line to the basin.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type Vec2 } from '../spec';

const W = 0.6;
const D = 0.45;
const HW = W / 2;
const HD = D / 2;
const BASIN_RADIUS_U = HW * 0.7;
const BASIN_CENTER_N = HD * 0.05;
const BASIN_RADIUS_N = HD * 0.65;
const BASIN_SEGMENTS = 20;
const TAP_HALF_U = HW * 0.12;
const TAP_FRONT = -HD + D * 0.12;

/** A closed ellipse outline as `segments` points (in the frame, at the default size). */
function ellipsePoints(centerN: number, radiusU: number, radiusN: number, segments: number): Vec2[] {
  const points: Vec2[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (2 * Math.PI * i) / segments;
    points.push([radiusU * Math.cos(a), centerN + radiusN * Math.sin(a)]);
  }
  return points;
}

export const sinkSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'POINT',
  constraint: { kind: 'WALL', defaultInset: 0 },
  defaultSize: { width: W, depth: D, height: 0.85 },
  params: [],
  shapes: [
    { id: 'counter', name: 'Counter', kind: 'rect', min: [-HW, -HD], max: [HW, HD] },
    { id: 'basin', name: 'Basin', kind: 'polyline', points: ellipsePoints(BASIN_CENTER_N, BASIN_RADIUS_U, BASIN_RADIUS_N, BASIN_SEGMENTS), closed: true },
    { id: 'tap', name: 'Tap', kind: 'rect', min: [-TAP_HALF_U, -HD], max: [TAP_HALF_U, TAP_FRONT] },
    {
      id: 'spout',
      name: 'Spout',
      kind: 'polyline',
      points: [
        [0, TAP_FRONT],
        [0, BASIN_CENTER_N - BASIN_RADIUS_N],
      ],
      closed: false,
    },
  ],
  groups: [{ id: 'sink', name: 'Sink', shapeIds: ['counter', 'basin', 'tap', 'spout'], resize: { u: 'stretch', n: 'stretch' } }],
  model3d: null,
};
